import React, { useState } from 'react';
import { useGetAllMachines, useCreateMachine } from '../hooks/useProductionPlanning';
import { getMexicoTomorrowString } from '../lib/mexicoDate';

interface MachineSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (data: { machineName: string; plannedStartDate: string; notes?: string }) => void;
  initialMachine?: string;
  initialDate?: string;
  initialNotes?: string;
}

const MachineSelectionModal: React.FC<MachineSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialMachine,
  initialDate,
  initialNotes,
}) => {
  // Calcular fecha de mañana por defecto (hora de México, no UTC)
  const getTomorrowDate = () => getMexicoTomorrowString();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMachineCode, setNewMachineCode] = useState('');
  const [newMachineName, setNewMachineName] = useState('');
  const [newMachineDescription, setNewMachineDescription] = useState('');
  const [newMachineLocation, setNewMachineLocation] = useState('');
  const [selectedMachine, setSelectedMachine] = useState<string>(initialMachine || '');
  const [plannedStartDate, setPlannedStartDate] = useState(initialDate || getTomorrowDate());
  const [notes, setNotes] = useState(initialNotes || '');

  // Actualizar valores cuando cambian los props iniciales
  React.useEffect(() => {
    if (isOpen) {
      setSelectedMachine(initialMachine || '');
      setPlannedStartDate(initialDate || getTomorrowDate());
      setNotes(initialNotes || '');
    }
  }, [isOpen, initialMachine, initialDate, initialNotes]);

  const { data: machines, isLoading } = useGetAllMachines();
  const createMachineMutation = useCreateMachine();

  const handleSelectExisting = () => {
    if (!selectedMachine) {
      alert('Por favor selecciona una máquina');
      return;
    }
    if (!plannedStartDate) {
      alert('Por favor selecciona una fecha planeada de inicio');
      return;
    }

    onSelect({
      machineName: selectedMachine,
      plannedStartDate,
      notes: notes.trim() || undefined,
    });
    resetForm();
    onClose();
  };

  const handleCreateNew = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMachineCode.trim()) {
      alert('El código del equipo es requerido');
      return;
    }
    if (!newMachineName.trim()) {
      alert('El nombre del equipo es requerido');
      return;
    }
    if (!plannedStartDate) {
      alert('Por favor selecciona una fecha planeada de inicio');
      return;
    }

    try {
      const newMachine = await createMachineMutation.mutateAsync({
        code: newMachineCode.trim(),
        name: newMachineName.trim(),
        description: newMachineDescription.trim() || undefined,
        location: newMachineLocation.trim() || undefined,
      });
      
      onSelect({
        machineName: newMachine.name,
        plannedStartDate,
        notes: notes.trim() || undefined,
      });
      resetForm();
      onClose();
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Error al crear el equipo');
    }
  };

  const resetForm = () => {
    setShowCreateForm(false);
    setNewMachineCode('');
    setNewMachineName('');
    setNewMachineDescription('');
    setNewMachineLocation('');
    setSelectedMachine('');
    setPlannedStartDate(getTomorrowDate());
    setNotes('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">📍 Seleccionar Máquina</h2>
          <button
            onClick={handleClose}
            className="text-white hover:bg-blue-700 rounded-full p-1 transition-colors"
            title="Cerrar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          {!showCreateForm ? (
            <div>
              <p className="text-gray-600 mb-4">
                Selecciona una máquina existente o crea una nueva:
              </p>

              {/* Máquinas existentes */}
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : machines && machines.length > 0 ? (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-800 mb-3">Máquinas disponibles:</h3>
                  <div className="grid gap-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {machines.map((machine) => (
                      <label
                        key={machine.id}
                        className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedMachine === machine.name
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="machine"
                          value={machine.name}
                          checked={selectedMachine === machine.name}
                          onChange={(e) => setSelectedMachine(e.target.value)}
                          className="mr-3 h-4 w-4 text-blue-600"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{machine.name}</div>
                          <div className="text-xs text-gray-400">Código: {machine.code}</div>
                          {machine.description && (
                            <div className="text-sm text-gray-500">{machine.description}</div>
                          )}
                          {machine.location && (
                            <div className="text-xs text-gray-400 mt-1">
                              Ubicación: {machine.location}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg mb-6">
                  <p className="text-gray-500">No hay máquinas registradas aún</p>
                </div>
              )}

              {/* Campos adicionales */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha planeada de inicio <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={plannedStartDate}
                    onChange={(e) => setPlannedStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observaciones (opcional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Notas o instrucciones especiales..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Botones */}
              <div className="flex gap-3">
                <button
                  onClick={handleSelectExisting}
                  disabled={!selectedMachine || !plannedStartDate}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    selectedMachine && plannedStartDate
                      ? 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  ✓ Confirmar Planeación
                </button>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="flex-1 py-3 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 active:scale-95 transition-all"
                >
                  + Crear Nuevo Equipo
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleCreateNew}>
              <h3 className="font-semibold text-gray-800 mb-4">Crear nuevo equipo:</h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newMachineCode}
                    onChange={(e) => setNewMachineCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ej: CNC-01"
                    autoFocus
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del equipo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newMachineName}
                    onChange={(e) => setNewMachineName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ej: Máquina CNC Principal"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción (opcional)
                  </label>
                  <input
                    type="text"
                    value={newMachineDescription}
                    onChange={(e) => setNewMachineDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ej: Máquina de corte por control numérico"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ubicación (opcional)
                  </label>
                  <input
                    type="text"
                    value={newMachineLocation}
                    onChange={(e) => setNewMachineLocation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ej: Nave 1, Área de Producción"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha planeada de inicio <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={plannedStartDate}
                    onChange={(e) => setPlannedStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observaciones (opcional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Notas o instrucciones especiales..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 py-3 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 active:scale-95 transition-all"
                >
                  ← Volver
                </button>
                <button
                  type="submit"
                  disabled={createMachineMutation.isPending}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    createMachineMutation.isPending
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700 active:scale-95'
                  }`}
                >
                  {createMachineMutation.isPending ? 'Creando...' : '💾 Crear y Seleccionar'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default MachineSelectionModal;
